import { apiFetch, apiUrl } from "@/core/lib/api";
import { ApiError } from "@/core/lib/incident-api";

export { ApiError };

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  let res: Response;
  try {
    res = await apiFetch(apiUrl(path), {
      credentials: "include",
      headers: { "Content-Type": "application/json", ...(init?.headers ?? {}) },
      ...init,
    });
  } catch (err) {
    if (err instanceof TypeError && /fetch/i.test(err.message)) {
      throw new ApiError("Can't reach the server.", 0);
    }
    throw err;
  }
  if (res.status === 204) return undefined as T;
  const data = await res.json().catch(() => null);
  if (!res.ok) {
    const message =
      (data && typeof data === "object" && "error" in data
        ? String((data as { error: unknown }).error)
        : null) ?? `Request failed (${res.status})`;
    throw new ApiError(message, res.status);
  }
  return data as T;
}

/**
 * Leave types are workspace-configured (`LeaveSettings.types`); this map only
 * remains as a label FALLBACK for the built-in keys when settings haven't
 * loaded or a type was deleted from the config.
 */
export const LEAVE_TYPE_LABELS: Record<string, string> = {
  annual: "Annual leave",
  sick: "Sick leave",
  unpaid: "Unpaid leave",
  compassionate: "Compassionate leave",
  study: "Study leave",
};

// ── Workspace leave policy (settings) ────────────────────────────────────────

/** One configurable leave type in the workspace leave policy. */
export type LeaveTypeConfig = {
  key: string;
  label: string;
  /** Default yearly entitlement — the live fallback when HR set no override. */
  defaultDays: string;
  enabled: boolean;
  /** Max unused days rolling into the next year ("0" = none). */
  carryOverMaxDays: string;
  /** When false (e.g. unpaid), requests never count against a balance. */
  requiresBalance: boolean;
};

export type LeaveSettings = {
  types: LeaveTypeConfig[];
  allowNegativeBalance: boolean;
  countBusinessDaysOnly: boolean;
  minNoticeDays: number;
  updatedAt: string;
};

export type UpdateLeaveSettingsBody = Omit<LeaveSettings, "updatedAt">;

export function fetchLeaveSettings(): Promise<LeaveSettings> {
  return request<LeaveSettings>("/api/leave/settings");
}

export function updateLeaveSettings(body: UpdateLeaveSettingsBody): Promise<LeaveSettings> {
  return request<LeaveSettings>("/api/leave/settings", {
    method: "PUT",
    body: JSON.stringify(body),
  });
}

// ── Per-employee balances ─────────────────────────────────────────────────────

/**
 * One employee's balance for a type + year, resolved against the policy:
 * `entitledDays` is the EFFECTIVE value; `isDefault` means it comes from the
 * workspace default (+ carry-over) rather than an explicit override.
 * Virtual rows (no stored override or usage yet) have `id: null`.
 */
export type LeaveBalance = {
  id: string | null;
  leaveType: string;
  year: number;
  entitledDays: string;
  explicitEntitledDays: string | null;
  isDefault: boolean;
  carryoverDays: string;
  usedDays: string;
  notes: string | null;
};

export type EmployeeLeaveBalances = {
  year: number;
  years: number[];
  balances: LeaveBalance[];
};

export type UpsertLeaveBalanceBody = {
  leaveType: string;
  year: number;
  /** `null` clears the override — the workspace default applies again. */
  entitledDays: string | null;
  usedDays?: string;
  notes?: string;
};

export function listLeaveBalances(
  employeeId: string,
  year?: number,
): Promise<EmployeeLeaveBalances> {
  const qs = year != null ? `?year=${year}` : "";
  return request<EmployeeLeaveBalances>(`/api/leave/${employeeId}${qs}`);
}

export function upsertLeaveBalance(
  employeeId: string,
  body: UpsertLeaveBalanceBody,
): Promise<LeaveBalance> {
  return request<LeaveBalance>(`/api/leave/${employeeId}`, {
    method: "PUT",
    body: JSON.stringify(body),
  });
}

export function deleteLeaveBalance(employeeId: string, id: string): Promise<{ ok: boolean }> {
  return request(`/api/leave/${employeeId}/${id}`, { method: "DELETE" });
}

// ── HR leave-management overview ─────────────────────────────────────────────

export type LeaveRequestStatus = "pending" | "approved" | "rejected" | "cancelled";

/** Another request overlapping a pending one's dates (approver warning). */
export type LeaveOverlap = {
  id: string;
  startDate: string | null;
  endDate: string | null;
  status: string;
};

/** A leave request as the HR Leave menu sees it (named + derived days/category). */
export type LeaveMgmtRequest = {
  id: string;
  employeeId: string;
  employeeName: string;
  type: "leave" | "study_leave";
  category: string | null;
  label: string;
  status: LeaveRequestStatus;
  startDate: string | null;
  endDate: string | null;
  days: number;
  year: number;
  reason: string | null;
  decisionNote: string | null;
  createdAt: string;
  /** Approver warnings — populated on pending requests only. */
  overlaps: LeaveOverlap[];
  /** Remaining balance before this request (entitled − used − other pending). */
  remainingBefore: number | null;
};

/** A leave balance row with the employee's name resolved (effective values). */
export type LeaveMgmtBalance = {
  id: string;
  employeeId: string;
  employeeName: string;
  leaveType: string;
  year: number;
  entitledDays: string;
  explicitEntitledDays: string | null;
  isDefault: boolean;
  carryoverDays: string;
  usedDays: string;
  notes: string | null;
};

export type LeaveOverview = {
  /** Server's today (ISO yyyy-mm-dd) so "on leave now" is computed consistently. */
  today: string;
  settings: LeaveSettings;
  requests: LeaveMgmtRequest[];
  balances: LeaveMgmtBalance[];
};

/** Workspace-wide leave data for the HR Leave menu. */
export function fetchLeaveOverview(): Promise<LeaveOverview> {
  return request<LeaveOverview>("/api/leave/overview");
}

/** Pending-leave count for the sidebar badge. */
export function fetchLeaveCounts(): Promise<{ pending: number }> {
  return request<{ pending: number }>("/api/leave/counts");
}
